import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import axios from '../../axios';
import classes from './Organization.module.css';
import OrganizationInviteModal from './OrganizationInviteModal';
import Member from './Member';

const Organization = ({ organizationData: initialOrganizationData, onOrganizationUpdate }) => {
  const [organizationData, setOrganizationData] = useState(initialOrganizationData || {
    companyName: '',
    organizationNumber: '',
    vatNumber: '',
    peppolId: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingPostalCode: '',
    billingTown: '',
    billingCountry: '',
    deliverySameAsBilling: true,
    deliveryAddressLine1: '',
    deliveryAddressLine2: '',
    deliveryPostalCode: '',
    deliveryTown: '',
    deliveryCountry: '',
    members: [],
    isOwner: false,
  });
  const [loading, setLoading] = useState(!initialOrganizationData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [accountData, setAccountData] = useState(null);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const cached = localStorage.getItem('countries');
        if (cached) {
          setCountries(JSON.parse(cached));
          return;
        }

        const response = await axios.get('/organization/countries');
        const countriesData = response.data.countries || [];
        setCountries(countriesData);
        localStorage.setItem('countries', JSON.stringify(countriesData));
      } catch (err) {
        console.error('Failed to load countries:', err);
        setCountries([]);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    axios.get('/account').then((response) => {
      setAccountData(response.data);
    });
  }, []);

  const isDataChanged = useMemo(() => {
    if (!initialOrganizationData) return false;

    const fields = [
      'companyName', 'vatNumber', 'peppolId',
      'billingAddressLine1', 'billingAddressLine2', 'billingPostalCode', 'billingTown', 'billingCountry',
      'deliverySameAsBilling',
      'deliveryAddressLine1', 'deliveryAddressLine2', 'deliveryPostalCode', 'deliveryTown', 'deliveryCountry',
    ];

    return fields.some((field) => organizationData[field] !== initialOrganizationData[field]);
  }, [organizationData, initialOrganizationData]);

  useEffect(() => {
    if (initialOrganizationData) {
      setOrganizationData({
        companyName: initialOrganizationData.companyName || '',
        organizationNumber: initialOrganizationData.organizationNumber || '',
        vatNumber: initialOrganizationData.vatNumber || '',
        peppolId: initialOrganizationData.peppolId || '',
        billingAddressLine1: initialOrganizationData.billingAddressLine1 || '',
        billingAddressLine2: initialOrganizationData.billingAddressLine2 || '',
        billingPostalCode: initialOrganizationData.billingPostalCode || '',
        billingTown: initialOrganizationData.billingTown || '',
        billingCountry: initialOrganizationData.billingCountry || '',
        deliverySameAsBilling: localStorage.getItem('deliverySameAsBilling') === 'true' ? true : false,
        deliveryAddressLine1: initialOrganizationData.deliveryAddressLine1 || '',
        deliveryAddressLine2: initialOrganizationData.deliveryAddressLine2 || '',
        deliveryPostalCode: initialOrganizationData.deliveryPostalCode || '',
        deliveryTown: initialOrganizationData.deliveryTown || '',
        deliveryCountry: initialOrganizationData.deliveryCountry || '',
        members: initialOrganizationData.members || [],
        isOwner: initialOrganizationData.isOwner || false,
      });
      setLoading(false);
    }
  }, [initialOrganizationData]);

  const handleInputChange = (field, value) => {
    setOrganizationData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const payload = {
        companyName: organizationData.companyName,
        vatNumber: organizationData.vatNumber,
        peppolId: organizationData.peppolId,
        billingAddressLine1: organizationData.billingAddressLine1,
        billingAddressLine2: organizationData.billingAddressLine2,
        billingPostalCode: organizationData.billingPostalCode,
        billingTown: organizationData.billingTown,
        billingCountry: organizationData.billingCountry,
      };

      if (!organizationData.deliverySameAsBilling) {
        payload.deliveryAddressLine1 = organizationData.deliveryAddressLine1;
        payload.deliveryAddressLine2 = organizationData.deliveryAddressLine2;
        payload.deliveryPostalCode = organizationData.deliveryPostalCode;
        payload.deliveryTown = organizationData.deliveryTown;
        payload.deliveryCountry = organizationData.deliveryCountry;
      } else {
        payload.deliveryAddressLine1 = '';
        payload.deliveryAddressLine2 = '';
        payload.deliveryPostalCode = '';
        payload.deliveryTown = '';
        payload.deliveryCountry = '';
      }

      const response = await axios.put('/organization', payload);

      const updatedData = {
        companyName: response.data.companyName || '',
        organizationNumber: response.data.organizationNumber || '',
        vatNumber: response.data.vatNumber || '',
        peppolId: response.data.peppolId || '',
        billingAddressLine1: response.data.billingAddressLine1 || '',
        billingAddressLine2: response.data.billingAddressLine2 || '',
        billingPostalCode: response.data.billingPostalCode || '',
        billingTown: response.data.billingTown || '',
        billingCountry: response.data.billingCountry || '',
        deliveryAddressLine1: response.data.deliveryAddressLine1 || '',
        deliveryAddressLine2: response.data.deliveryAddressLine2 || '',
        deliveryPostalCode: response.data.deliveryPostalCode || '',
        deliveryTown: response.data.deliveryTown || '',
        deliveryCountry: response.data.deliveryCountry || '',
        members: response.data.members || [],
        isOwner: response.data.isOwner !== undefined ? response.data.isOwner : organizationData.isOwner,
      };
      setOrganizationData(updatedData);

      if (onOrganizationUpdate) {
        onOrganizationUpdate(response.data);
      }

      setSuccessMessage('Organization updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const openInviteModal = () => {
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
  };

  const handleInviteSuccess = (message) => {
    if (message) {
      setSuccessMessage(message);
    }
    setInviteModalOpen(false);
  };

  const handleUpdateMemberRole = async (memberId, nextRoles) => {
    if (!memberId) return;
    const rolesArray = Array.isArray(nextRoles) ? nextRoles : [nextRoles];
    if (rolesArray.length === 0) {
      setError('Please select at least one role');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const response = await axios.put(`/organization/members/${memberId}`, {
        roles: rolesArray,
      });

      const updatedRoles = response.data?.roles;
      if (Array.isArray(updatedRoles)) {
        setOrganizationData((prev) => ({
          ...prev,
          members: (prev.members || []).map((m) =>
            (m?._id || m?.id) === memberId ? { ...m, roles: updatedRoles } : m
          ),
        }));
      }

      setSuccessMessage('Member role updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update member role');
    } finally {
      setSaving(false);
    }
  };

  const changeSameAsBilling = () => {
    localStorage.setItem('deliverySameAsBilling', !organizationData.deliverySameAsBilling);
    setOrganizationData((prev) => ({
      ...prev,
      deliverySameAsBilling: !prev.deliverySameAsBilling,
    }));
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <span>Loading...</span>
      </div>
    );
  }

  const isOwner = accountData?.roles?.some((role) => role === 'owner');
  const isAdministration = accountData?.roles?.some((role) => role === 'administration');
  const canEdit = isOwner || isAdministration;

  return (
    <>
      <div className={classes.organizationForm}>
        {error && <div className={classes.error}>{error}</div>}
        {successMessage && <div className={classes.success}>{successMessage}</div>}

        <div className={classes.formRow}>
          <label className={classes.label}>Company</label>
          {canEdit ? (
            <input
              type="text"
              className={classes.input}
              value={organizationData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              disabled={!canEdit}
            />
          ) : (
            <span className={classes.value}>{organizationData.companyName}</span>
          )}
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>Organization number</label>
          <span className={classes.value}>{organizationData.organizationNumber || '—'}</span>
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>VAT number</label>
          <input
            type="text"
            className={classes.input}
            value={organizationData.vatNumber}
            onChange={(e) => handleInputChange('vatNumber', e.target.value)}
            placeholder="e.g., BE0123456789"
            disabled={!canEdit}
          />
        </div>

        {organizationData.billingCountry === 'Belgium' && (
          <div className={classes.formRow}>
            <label className={classes.label}>Peppol ID</label>
            <input
              type="text"
              className={classes.input}
              value={organizationData.peppolId}
              onChange={(e) => handleInputChange('peppolId', e.target.value)}
              disabled={!canEdit || organizationData.billingCountry !== 'Belgium'}
            />
          </div>
        )}

        <h3 className={classes.sectionTitle}>Billing Address</h3>

        <div className={classes.formRow}>
          <label className={classes.label}>Address line 1</label>
          <input
            type="text"
            className={classes.input}
            value={organizationData.billingAddressLine1}
            onChange={(e) => handleInputChange('billingAddressLine1', e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>Address line 2</label>
          <input
            type="text"
            className={classes.input}
            value={organizationData.billingAddressLine2}
            onChange={(e) => handleInputChange('billingAddressLine2', e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>Postal code</label>
          <input
            type="text"
            className={classes.input}
            value={organizationData.billingPostalCode}
            onChange={(e) => handleInputChange('billingPostalCode', e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>Town</label>
          <input
            type="text"
            className={classes.input}
            value={organizationData.billingTown}
            onChange={(e) => handleInputChange('billingTown', e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className={classes.formRow}>
          <label className={classes.label}>Country</label>
          <Select
            value={countries.find((c) => c.value === organizationData.billingCountry)}
            onChange={(option) => handleInputChange('billingCountry', option?.value || '')}
            options={countries}
            isClearable
            isSearchable
            isDisabled={!canEdit}
            placeholder="Select country..."
            className={classes.selectContainer}
            classNamePrefix="select"
          />
        </div>

        <div className={classes.formRow}>
          <label className={classes.checkboxLabel}>
            <input
              type="checkbox"
              checked={organizationData.deliverySameAsBilling}
              onChange={changeSameAsBilling}
              disabled={!canEdit}
            />
            <span>Delivery address same as billing address</span>
          </label>
        </div>

        {!organizationData.deliverySameAsBilling && (
          <div>
            <h3 className={classes.sectionTitle}>Delivery Address</h3>

            <div className={classes.formRow}>
              <label className={classes.label}>Address line 1</label>
              <input
                type="text"
                className={classes.input}
                value={organizationData.deliveryAddressLine1}
                onChange={(e) => handleInputChange('deliveryAddressLine1', e.target.value)}
                disabled={!canEdit || organizationData.deliverySameAsBilling}
              />
            </div>

            <div className={classes.formRow}>
              <label className={classes.label}>Address line 2</label>
              <input
                type="text"
                className={classes.input}
                value={organizationData.deliveryAddressLine2}
                onChange={(e) => handleInputChange('deliveryAddressLine2', e.target.value)}
                disabled={!canEdit || organizationData.deliverySameAsBilling}
              />
            </div>

            <div className={classes.formRow}>
              <label className={classes.label}>Postal code</label>
              <input
                type="text"
                className={classes.input}
                value={organizationData.deliveryPostalCode}
                onChange={(e) => handleInputChange('deliveryPostalCode', e.target.value)}
                disabled={!canEdit || organizationData.deliverySameAsBilling}
              />
            </div>

            <div className={classes.formRow}>
              <label className={classes.label}>Town</label>
              <input
                type="text"
                className={classes.input}
                value={organizationData.deliveryTown}
                onChange={(e) => handleInputChange('deliveryTown', e.target.value)}
                disabled={!canEdit || organizationData.deliverySameAsBilling}
              />
            </div>

            <div className={classes.formRow}>
              <label className={classes.label}>Country</label>
              <Select
                value={countries.find((c) => c.value === organizationData.deliveryCountry)}
                onChange={(option) => handleInputChange('deliveryCountry', option?.value || '')}
                options={countries}
                isClearable
                isSearchable
                isDisabled={!canEdit || organizationData.deliverySameAsBilling}
                placeholder="Select country..."
                className={classes.selectContainer}
                classNamePrefix="select"
              />
            </div>
          </div>
        )}

        {canEdit && isDataChanged && (
          <div className={classes.buttonRow}>
            <button
              className={classes.saveButton}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        <div className={classes.membersSection}>
          <h2 className={classes.membersTitle}>Members</h2>
          <div className={classes.membersTable}>
            {organizationData.members && organizationData.members.length > 0 ? (
              organizationData.members.map((member) => (
                <Member
                  key={member?._id || member?.id || member?.email}
                  member={member}
                  saving={saving}
                  onSaveRole={handleUpdateMemberRole}
                  canEdit={isOwner}
                />
              ))
            ) : (
              <div className={classes.noMembers}>No members found</div>
            )}
          </div>
          {isOwner && (
            <div className={classes.inviteButtonRow}>
              <button
                className={classes.saveButton}
                onClick={openInviteModal}
                disabled={saving}
              >
                Invite member
              </button>
            </div>
          )}
        </div>
      </div>

      <OrganizationInviteModal
        isOpen={inviteModalOpen}
        onClose={closeInviteModal}
        onInviteSuccess={handleInviteSuccess}
      />
    </>
  );
};

export default Organization;
